import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { ErrorHandler } from '../utils/errorHandler';
import authService from '../services/authService';

const AdminRoute = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        
        // Verificar se existe token
        if (!token) {
          setIsAuthenticated(false);
          setIsAdmin(false);
          setLoading(false);
          return;
        }

        // Verificar se o token é válido estruturalmente
        if (!ErrorHandler.isTokenValid(token)) {
          console.warn('Token inválido ou expirado no AdminRoute');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setIsAuthenticated(false);
          setIsAdmin(false);
          setLoading(false);
          return;
        }

        setIsAuthenticated(true);

        // Tentar buscar perfil do usuário para verificar se é admin
        try {
          const profile = await authService.getUserProfile();
          const adminStatus = profile.isAdmin || profile.role === 'admin';
          setIsAdmin(adminStatus);
          
          // Atualizar dados do usuário no localStorage
          authService.updateUserData(profile);
          
        } catch (apiError) {
          ErrorHandler.logError(apiError, 'AdminRoute - API Profile');
          
          // Se for erro de autenticação, redirecionar
          if (apiError.response?.status === 401 || apiError.response?.status === 403) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setIsAuthenticated(false);
            setIsAdmin(false);
            setLoading(false);
            return;
          }
          
          // Fallback: verificar no token ou localStorage
          try {
            const tokenData = JSON.parse(atob(token.split(".")[1]));
            const adminFromToken = tokenData.isAdmin || tokenData.role === 'admin';
            
            // Verificar também no localStorage
            const userData = authService.getCurrentUser();
            const adminFromStorage = userData && (userData.isAdmin || userData.role === 'admin');
            
            setIsAdmin(adminFromToken || adminFromStorage);
            
            if (!adminFromToken && !adminFromStorage) {
              setError('Não foi possível verificar permissões de administrador.');
            }
          } catch (tokenError) {
            ErrorHandler.logError(tokenError, 'AdminRoute - Token Decode');
            setIsAdmin(false);
            setError('Erro ao verificar permissões. Por favor, faça login novamente.');
          }
        }
      } catch (error) {
        ErrorHandler.logError(error, 'AdminRoute - General');
        setError('Erro ao verificar permissões de acesso.');
        setIsAuthenticated(false);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkAdminStatus();
  }, []);
  
  // Mostrar loading enquanto verifica permissões
  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>Verificando permissões de administrador...</p>
      </div>
    );
  }

  // Redirecionar para login se não estiver autenticado
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ 
      message: 'Por favor, faça login para acessar a área administrativa.' 
    }} />;
  }

  // Mostrar erro se houver problema na verificação
  if (error) {
    return (
      <div className="admin-error">
        <div className="error-message">
          <h3>Erro de Acesso</h3>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Tentar Novamente</button>
        </div>
      </div>
    );
  }

  // Redirecionar para dashboard se não for administrador
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace state={{ 
      message: 'Acesso negado. Você não tem permissões de administrador.' 
    }} />;
  }

  // Renderizar componente admin se todas as validações passaram
  return children;
};

export default AdminRoute;
